/**
 * @voltxampere/ui — WaveformRenderer (Sprint 15)
 *
 * Parallel ikinci instance of the schematic renderer pattern,
 * tuned for the time × voltage domain (ADR-0023). Owns its own
 * rAF scheduler, dirty tracker, and signal effect — independent
 * of the schematic canvas so a paused waveform does not block a
 * running schematic and vice versa.
 *
 * Frame composition:
 *   1. Clear panel background.
 *   2. Draw the time × voltage grid.
 *   3. For each enabled channel: snapshot the sample buffer,
 *      decimate to ≤ 2 × canvasWidth via LTTB, stroke the trace.
 *   4. Overlay trigger threshold + arrow if a trigger fired this
 *      sweep.
 *   5. (Sprint 17+) horizontal cursor overlay.
 *
 * The renderer is decoupled from the channel store and viewport
 * via dependency injection so unit tests construct it against
 * pure-function fakes; integration is wired in `<vxa-waveform>`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
import { effect } from "@preact/signals-core";
import { rafScheduler, FRAME_BUDGET_MS } from "../canvas/renderer.js";
import { lttb } from "./decimation.js";
import { drawWaveformGrid } from "./draw/grid.js";
import { drawTrace } from "./draw/trace.js";
import { drawTriggerArrow, drawTriggerLine } from "./draw/trigger-marker.js";
import { CHANNEL_IDS, TIME_DIVISION_COUNT, V_DIVISION_COUNT } from "./types.js";
export class WaveformRenderer {
    deps;
    dpr = 1;
    rafId = null;
    disposeEffect;
    scheduler;
    constructor(deps) {
        this.deps = deps;
        this.scheduler = deps.scheduler ?? rafScheduler;
    }
    start() {
        this.disposeEffect = effect(() => {
            // Subscribe to every signal that should trigger a redraw.
            void this.deps.channelsSignal.value;
            void this.deps.viewport.timeBase.value;
            void this.deps.viewport.timeOffset.value;
            void this.deps.viewport.vDivVersion.value;
            void this.deps.viewport.vOffsetVersion.value;
            void this.deps.themeSignal.value;
            void this.deps.triggerSignal.value;
            // Sprint 18 D3 — live flag (re-render when toggled).
            void this.deps.liveSignal?.value;
            this.deps.dirty.markAllDirty();
            this.scheduleFrame();
        });
        return () => {
            this.dispose();
        };
    }
    dispose() {
        if (this.rafId !== null) {
            this.scheduler.cancel(this.rafId);
            this.rafId = null;
        }
        this.disposeEffect?.();
        this.disposeEffect = undefined;
    }
    setDPR(dpr) {
        if (!Number.isFinite(dpr) || dpr <= 0)
            return;
        this.dpr = dpr;
        this.deps.dirty.markAllDirty();
        this.scheduleFrame();
    }
    invalidate() {
        this.deps.dirty.markAllDirty();
        this.scheduleFrame();
    }
    invalidateChannel(id) {
        this.deps.dirty.markChannel(id);
        this.scheduleFrame();
    }
    /** Synchronously draw one frame. Tests step the loop via this
     *  entry point so they don't depend on rAF. */
    tick(time) {
        this.rafId = null;
        void time;
        if (this.deps.dirty.isClean)
            return;
        const start = performance.now();
        const { full } = this.deps.dirty.consume();
        const cssW = this.deps.canvas.width / this.dpr;
        const cssH = this.deps.canvas.height / this.dpr;
        this.deps.ctx.save();
        this.deps.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        if (full) {
            this.deps.ctx.fillStyle = this.deps.themeSignal.value.bg.void;
            this.deps.ctx.fillRect(0, 0, cssW, cssH);
        }
        this.drawComposite(cssW, cssH);
        this.deps.ctx.restore();
        const drawMs = performance.now() - start;
        if (drawMs > FRAME_BUDGET_MS) {
            console.warn(`[VXA waveform] Frame draw took ${drawMs.toFixed(1)}ms`);
        }
    }
    scheduleFrame() {
        if (this.rafId !== null)
            return;
        this.rafId = this.scheduler.schedule((t) => {
            this.tick(t);
        });
    }
    drawComposite(cssW, cssH) {
        const theme = this.deps.themeSignal.value;
        drawWaveformGrid(this.deps.ctx, theme, {
            canvasWidth: cssW,
            canvasHeight: cssH,
            timeDivCount: TIME_DIVISION_COUNT,
            voltageDivCount: V_DIVISION_COUNT,
        });
        const viewport = this.deps.viewport;
        const timeToScreen = (t) => viewport.timeToScreen(t, cssW);
        const voltageToScreen = (v, id) => viewport.voltageToScreen(v, id, cssH);
        const projector = { timeToScreen, voltageToScreen };
        const channels = this.deps.channelsSignal.value;
        const decimateThreshold = Math.max(2, Math.ceil(cssW * 2));
        const live = this.deps.liveSignal?.value ?? false;
        // Phase derived from frame time so the marker pulses gently
        // (~1.6 s loop). When `prefers-reduced-motion: reduce` is
        // active we hold the phase at 0.5 so the marker still
        // appears (signalling "live") but never pulses.
        const reduceMotion = typeof globalThis.matchMedia === "function" &&
            globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const livePhase = live ? (reduceMotion ? 0.5 : (performance.now() / 1600) % 1) : 0;
        for (const id of CHANNEL_IDS) {
            const view = channels.get(id);
            if (!view?.enabled)
                continue;
            const snap = view.buffer.snapshot();
            if (snap.times.length === 0)
                continue;
            const decimated = lttb(snap.times, snap.values, decimateThreshold);
            drawTrace(this.deps.ctx, {
                channelId: id,
                times: decimated.times,
                values: decimated.values,
                canvasWidth: cssW,
                canvasHeight: cssH,
                projector,
                theme,
                live,
                livePhase,
            });
        }
        const trigger = this.deps.triggerSignal.value;
        if (trigger) {
            drawTriggerLine(this.deps.ctx, {
                threshold: trigger.threshold,
                channelId: trigger.source,
                canvasWidth: cssW,
                canvasHeight: cssH,
                voltageToScreen,
                theme,
            });
            if (trigger.eventTime !== null) {
                drawTriggerArrow(this.deps.ctx, {
                    time: trigger.eventTime,
                    canvasWidth: cssW,
                    canvasHeight: cssH,
                    timeToScreen,
                    theme,
                });
            }
        }
    }
}
//# sourceMappingURL=renderer.js.map